/** @jsx jsx */
import { jsx, useThemeUI, Text, Flex, Box } from "theme-ui";
import React, { useContext, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import useInterval from "hooks/useInterval";
import constants from "config/constants";
import { useModal } from "react-modal-hook";
import Dialog from "components/Dialog/Dialog";
import { Trans } from "@lingui/react";
import Button from "components/Button/Button";
import { customAlphabet } from "nanoid";
import * as api from "api/api";
import { useQuery, useMutation, UseQueryResult } from "react-query";
import { useToasts } from "react-toast-notifications";
import { getErrorResponse } from "utils/common";
import { useAppDispatch, useAppSelector } from "store/store";
import { setLicense } from "store/license";
import { isAuthorisationDone, isAuthorised } from "store/authorisation";
import { AuthenticationContext } from "providers/AuthenticationProvider/AuthenticationProvider";

const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const nanoidShort = customAlphabet(ALPHABET, 6);

interface ProviderProps {
    children: React.ReactNode;
    companyId?: string;
}

interface ContextProps {
    licenceId: string;
    license: any;
    handleCreateLicense: any;
    licenses: any;
    licenseTypes: any;
    customCode: any;

    billingInformation: any;
}

export const LicenceContext = React.createContext<ContextProps>({
    licenceId: "",
    billingInformation: null,
    handleCreateLicense: () => { },
    license: undefined,
    licenses: undefined,
    licenseTypes: undefined,
    customCode: "",
});

const CORRECT_LICENSE_TYPE_ID = 15;
const UPDATE_INTERVAL = 1000 * 60 * 60 * 24;

const shouldLoad = (state: UseQueryResult) =>
    !state.isError && !state.data && !state.isLoading;

export const useLicence = () => useContext(LicenceContext);

const LicenceProvider = ({ children }: ProviderProps) => {
    const { addToast } = useToasts();
    const { theme } = useThemeUI();
    const history = useHistory();
    const licenseId = useAppSelector(s => s.license.entity?.license);
    const oldLicenseId = useAppSelector(s => s.license.entity?.oldLicense);
    const dispatch = useAppDispatch();
    const customCode = nanoidShort();
    const authorised = useAppSelector(isAuthorised);
    const companyId = useAppSelector(s => s.authorisation.entity?.companyId);
    const authorisationFinished = useAppSelector(isAuthorisationDone);
    const { disconnect } = useContext(AuthenticationContext);

    const licenseTypes = useQuery("licenseTypes", () =>
        api.getLicenseTypes({
            params: {
                CountryId: "SE",
                ExtraLicenseOption: true,
                IncludeLicenseItems: false,
                IncludeLicensePrices: true,
            },
        }), {
        refetchOnWindowFocus: false,
    }
    );

    const licenses = useQuery("licenses", () => api.getLicenses(
        {
            // @ts-ignore
            CompanyId: companyId,
            OnlyActiveLicenses: true,
            TypeId: 15
        }), {
        refetchOnWindowFocus: false,
        enabled: !!companyId
    });

    const billingInformation = useQuery("billingInformation", () => api.getBillingInformation(), {
        refetchOnWindowFocus: false,
        enabled: authorised
    });

    const newLicense = useMutation(
        "createLicense",
        (data: any) => api.createLicense({}, data),
        {
            onSuccess: (data) => {
                licenses.refetch();
                dispatch(setLicense(data.MetaData));
            },
            onError: (err) => {
                addToast(getErrorResponse(err), {
                    appearance: "error",
                    autoDismiss: true,
                });
            },
        }
    );

    const cancelLicense = useMutation(
        "cancelLicense",
        (data: BokaMera.CancelLicense) => api.cancelLicense({}, data),
        {
            onSuccess: () => {
                licenses.refetch();
            },
            onError: (err) => {
                addToast(getErrorResponse(err), {
                    appearance: "error",
                    autoDismiss: true,
                });
            },
        }
    );

    const updateLicense = useCallback(() => {
        if (
            constants.MODE !== "demo" &&
            licenses.data &&
            licenseId &&
            Array.isArray(licenses.data) &&
            licenses.data?.filter(
                (l: any) =>
                    l.TypeId === CORRECT_LICENSE_TYPE_ID && l.MetaData === licenseId
            ).length === 0
        ) {
            showLicenseActModal();
        } else {
            hideLicenseActModal();
        }
    }, [licenses, licenseId]);

    // Effectively when component mounts with necessary dependencies
    useEffect(() => {
        if (companyId && shouldLoad(licenses)) {
            licenses.refetch()
        }
    }, [companyId, licenses, licenseId]);

    // find if there are licenses on the server
    useEffect(() => {
        if (licenses.data) {
            updateLicense();
        }
    }, [licenses, updateLicense]);

    // Refresh license every 24 hours
    useInterval(() => {
        if (companyId && licenses.data && !licenses.isLoading) {
            licenses.refetch();
        }
        updateLicense();
    }, UPDATE_INTERVAL);

    // act -> sucess modals effect
    useEffect(() => {
        if (oldLicenseId && licenseId && oldLicenseId !== licenseId) {
            showLicenseSucessModal();
        }

        // Delete old license
        if (newLicense.data && oldLicenseId && oldLicenseId !== licenseId) {
            const oldLicense = licenses.data?.find((license: BokaMera.Licence) => license.MetaData === oldLicenseId);
            if (oldLicense?.Id) {
                cancelLicense.mutate({ Id: oldLicense.Id });
            }
        }
    }, [oldLicenseId, licenseId, newLicense, licenses]);


    useEffect(() => {
        if (!authorisationFinished) return;

        if (authorised && history.location.pathname !== 'license') {
            history.push(`/${companyId}`)
        }
    }, [authorisationFinished, authorised]);

    const handleCreateLicense = () => {
        newLicense.mutate({
            CompanyId: companyId,
            MetaData: customCode,
            TypeId: "15",
            BillingInformation: billingInformation.data,
        });
    };

    const [showLicenseActModal, hideLicenseActModal] = useModal(() => (
        <Dialog //
            // @ts-ignore
            style={{ paddingBottom: "3", minWidth: theme?.sizes["3/4"] }}
            onClose={() => { }}

        >
            <Box sx={{ textAlign: "center" }}>
                <Text as="h3" sx={{ mb: "4" }}>
                    <Trans id="license.problemWithYourLicense.title" />
                </Text>
                <Text>
                    <Trans id="license.problemWithYourLicense.description" />
                </Text>
            </Box>
            <Flex sx={{ justifyContent: "center", mb: "4" }}>
                <Trans id="license.licenseId" /> {licenseId}
            </Flex>

            <Flex sx={{ justifyContent: "center" }}>
                <Flex>
                    <Button
                        variant="outline"
                        onClick={() => window.location.reload()}
                        style={{ minWidth: "240px", ml: "2", mr: "2" }}
                    >
                        <Trans id="license.tryAgain" />
                    </Button>
                    <Button
                        data-testid="newLicense"
                        disabled={newLicense.isLoading}
                        style={{ minWidth: "240px", ml: "2", mr: "2" }}
                        onClick={() => {
                            newLicense.mutate(
                                {
                                    CompanyId: companyId,
                                    MetaData: nanoidShort() as string,
                                    TypeId: "15",
                                    BillingInformation: billingInformation.data,
                                }
                            )
                        }}
                    >
                        <Trans id="license.addNewLicense" />
                    </Button>
                    <Button
                        variant="danger"
                        style={{ minWidth: "240px", ml: "2", mr: "2" }}
                        onClick={() => {
                            disconnect();

                            if (history.location.pathname !== "/login") {
                                history.push("/login");
                            }
                            hideLicenseActModal();
                            window.location.reload();
                        }}
                    >
                        <Trans id="UserButtons.disconnectDisplay" />
                    </Button>
                </Flex>
            </Flex>
        </Dialog>
    ), [licenseId]);

    const [showLicenseSucessModal, hideLicenseSucessModal] = useModal(() => {
        return <Dialog //
            // @ts-ignore
            style={{ paddingBottom: "3", minWidth: theme?.sizes["3/4"] }}
            onClose={() => { }}
        >
            <Box data-testid="newLicenseSuccess" >
                <Box sx={{ textAlign: "center", mb: "3" }}>
                    <Text as="h3" >
                        <Trans id="license.problemWithYourLicense.sucess.title" />
                    </Text>
                </Box>

                <Flex sx={{ justifyContent: "center" }}>
                    <Trans id="license.newLicenseId" /> {licenseId}
                </Flex>

                <Box sx={{ textAlign: "center", mt: "4" }}>
                    <Button
                        style={{ minWidth: "240px", ml: "2", mr: "2" }}
                        onClick={() => {
                            updateLicense();
                            hideLicenseSucessModal();
                            window.location.reload();
                        }}
                    >
                        <Trans id="license.problemWithYourLicense.continueUsingTheApp" />
                    </Button>
                </Box>
            </Box>
        </Dialog>
    }, [licenseId]);

    return (
        <LicenceContext.Provider
            value={{
                licenceId: licenseId || "",
                license: newLicense,
                licenses: licenses,
                licenseTypes: licenseTypes,
                billingInformation,
                handleCreateLicense,
                customCode,
            }}
        >
            {children}
        </LicenceContext.Provider>
    );
};

export default LicenceProvider;
